import { z } from "zod";
import { ShortText, idString } from "./common.js";
const ProblemTreeNodeText = z
    .string()
    .min(0)
    .max(80)
    .describe("A short sticky-note label containing one symptom or one potential cause. Use one idea only, prefer eight words or fewer, and exclude evidence, trend commentary, and explanations.");
/**
 * The three states a root-cause sticky can hold. Exactly three, deliberately:
 * a cause is a hypothesis until evidence settles it either way. Stored on the
 * cause itself rather than in a parallel evidence model.
 */
export const PROBLEM_TREE_CAUSE_STATUSES = [
    "suspected",
    "confirmed",
    "ruled_out",
];
export const ProblemTreeCauseStatusSchema = z
    .enum(PROBLEM_TREE_CAUSE_STATUSES)
    .describe('Evidence status of this potential cause. "suspected" — plausible, evidence is insufficient; "confirmed" — evidence supports it; "ruled_out" — evidence contradicts it. Agreement, plausibility, repetition, or being the last answer in a Why chain is never confirmation. Omit the field to leave a cause suspected, and repeat the stored status of every cause you are not deliberately changing.');
const ProblemTreeEffectSchema = z
    .object({
    id: idString,
    text: ProblemTreeNodeText,
})
    .strict();
/**
 * A cause node. `status` is OPTIONAL so every payload written before statuses
 * existed still validates and reads back as suspected; nothing migrates.
 */
const ProblemTreeCauseSchema = z
    .object({
    id: idString,
    text: ProblemTreeNodeText,
    status: ProblemTreeCauseStatusSchema.optional(),
})
    .strict();
function problemTreeParamsSchema(effect, cause) {
    return z
        .object({
        effects: z
            .array(effect)
            .max(16)
            .describe("Distinct symptoms or effects. Each item renders as one sticky note."),
        problem: ShortText,
        causes: z
            .array(cause)
            .max(16)
            .describe("Distinct potential causes. Each item renders as one sticky note, carrying its own evidence status."),
    })
        .strict();
}
/**
 * Problem Tree — Specify, singleton. Visual tree with three regions:
 * effects (foliage at top), problem statement (trunk), causes (roots).
 * Renders inside a ReactFlow canvas in the Component.
 */
/** Canonical schema. Sticky labels contain one concise idea. */
export const ProblemTreeParamsSchema = problemTreeParamsSchema(ProblemTreeEffectSchema, ProblemTreeCauseSchema);
/**
 * The one place a missing status becomes a displayed one. A cause authored
 * before statuses existed, or written by a model that omitted the field, is
 * suspected — never silently confirmed.
 */
export function problemTreeCauseStatus(cause) {
    return cause.status ?? "suspected";
}
export const defaultProblemTreeParams = {
    effects: [],
    problem: "Problem statement",
    causes: [],
};
//# sourceMappingURL=problemTree.js.map